import { StrapiItem, StrapiAttributes, StrapiListResponse } from '../types/strapi.types';
import { Team, TeamAttributes, TeamsApiResponse, TeamsQueryResult } from '../types/team.types';

/**
 * Normaliza un item de Strapi a un formato plano
 * Combina el id con los attributes en un solo objeto
 */
export function normalizeStrаpiItem<
  TAttributes extends StrapiAttributes,
  TResult extends { id: number },
>(
  item: StrapiItem<TAttributes>,
  transform: (id: number, attributes: TAttributes) => TResult,
): TResult {
  return transform(item.id, item.attributes);
}

/**
 * Normaliza una lista de items de Strapi
 */
export function normalizeStrapiList<
  TAttributes extends StrapiAttributes,
  TResult extends { id: number },
>(
  response: StrapiListResponse<TAttributes>,
  transform: (id: number, attributes: TAttributes) => TResult,
): TResult[] {
  return response.data.map((item) => normalizeStrаpiItem(item, transform));
}

/**
 * Transforma los atributos de un equipo de Strapi a formato normalizado
 */
export function transformTeamAttributes(id: number, attributes: TeamAttributes): Team {
  return {
    id,
    nombre: attributes.nombre,
    color: attributes.color,
    createdAt: new Date(attributes.createdAt),
    updatedAt: new Date(attributes.updatedAt),
  };
}

/**
 * Normaliza la respuesta completa de equipos de Strapi
 * Incluye datos normalizados y paginación
 */
export function normalizeTeamsResponse(response: TeamsApiResponse): TeamsQueryResult {
  return {
    data: normalizeStrapiList(response, transformTeamAttributes),
    pagination: {
      page: response.meta.pagination.page,
      pageSize: response.meta.pagination.pageSize,
      pageCount: response.meta.pagination.pageCount,
      total: response.meta.pagination.total,
    },
  };
}

/**
 * Normaliza una respuesta genérica de Strapi con un transformador personalizado
 */
export function createNormalizer<
  TAttributes extends StrapiAttributes,
  TResult extends { id: number },
>(transform: (id: number, attributes: TAttributes) => TResult) {
  return (response: StrapiListResponse<TAttributes>) => ({
    data: normalizeStrapiList(response, transform),
    pagination: {
      page: response.meta.pagination.page,
      pageSize: response.meta.pagination.pageSize,
      pageCount: response.meta.pagination.pageCount,
      total: response.meta.pagination.total,
    },
  });
}
